import React, { useEffect, useRef, useState } from 'react';
import qs from 'qs';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { track } from '@vercel/analytics';
import { useLoading } from '../../context/LoadingContext';
import SmsConsentFieldset, { smsConsentSubmissionFields } from './SmsConsentFieldset';
import '../Pages/Why.css';

const PORTAL_ID = '45645086';

function ArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

function FormErrorState({ message, onBack }) {
  return (
    <div className="why-system-page grid-bg contact-form-page">
      <section className="sec contact-form-page__main">
        <div className="wrap">
          <div className="contact-form-page__card contact-form-page__card--center">
            <span className="kicker">Contact</span>
            <h1 className="h2">Something went wrong</h1>
            <p className="lede">{message}</p>
            <div className="contact-form-page__actions">
              <button type="button" className="btn btn-primary lg" onClick={onBack}>
                Go back
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function renderFieldInput(field, formValues, handlers) {
  const { handleInputChange, handleInputBlur } = handlers;
  const common = {
    name: field.name,
    onChange: handleInputChange,
    onBlur: handleInputBlur,
    required: field.required,
    className: 'cx-input',
  };

  if (field.name === 'email') {
    return (
      <input
        type="email"
        {...common}
        value={formValues.email || ''}
        placeholder={field.placeholder}
      />
    );
  }

  if (field.name === 'phone') {
    return (
      <input
        type="tel"
        {...common}
        value={formValues.phone || ''}
        placeholder={field.placeholder}
      />
    );
  }

  if (field.name === 'accredited_investor') {
    return (
      <select {...common} value={formValues[field.name] || ''}>
        <option value="">Please select</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>
    );
  }

  if (field.fieldType === 'select') {
    return (
      <select {...common} value={formValues[field.name] || ''}>
        <option value="">{field.placeholder}</option>
        {field.options?.map((option, idx) => (
          <option key={idx} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  if (field.fieldType === 'textarea' || field.name === 'message') {
    return (
      <textarea
        {...common}
        value={formValues[field.name] || ''}
        placeholder={field.placeholder}
        rows={4}
      />
    );
  }

  return (
    <input
      type={field.fieldType === 'number' ? 'number' : 'text'}
      {...common}
      value={formValues[field.name] || ''}
      placeholder={field.placeholder}
    />
  );
}

export default function HubspotContactForm({ config }) {
  const {
    formType,
    kicker,
    title,
    titleAccent,
    lede,
    featured,
    successTitle,
    successMessage,
    pageTitle,
  } = config;

  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [formFields, setFormFields] = useState([]);
  const [formValues, setFormValues] = useState({});
  // A2P SMS consent (docs/A2P_SMS_COMPLIANCE.md §1) — optional, unchecked by default.
  const [smsConsent, setSmsConsent] = useState({ marketing: false, nonMarketing: false });
  const [error, setError] = useState(null);
  const formRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const formId = location.pathname.split('/').pop();
  const { loading, setLoading } = useLoading();
  const hasPhoneField = formFields.some((field) => field.name === 'phone');

  const getUserIP = async () => {
    try {
      const response = await axios.get('https://api.ipify.org?format=json');
      return response.data.ip;
    } catch (err) {
      console.error("Failed to fetch the user's IP address:", err);
      return null;
    }
  };

  const getHubSpotCookie = () => {
    const name = 'hubspotutk=';
    const decodedCookie = decodeURIComponent(document.cookie);
    for (const part of decodedCookie.split(';')) {
      const cookie = part.trim();
      if (cookie.startsWith(name)) {
        return cookie.substring(name.length);
      }
    }
    return '';
  };

  useEffect(() => {
    const fetchFormFields = async () => {
      try {
        const response = await axios.get(`/api/getFormData?formGuid=${formId}&portalId=${PORTAL_ID}`);
        const fields = response.data?.formFields.filter((x) => !x.hidden);
        setFormFields(fields);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch form fields. Please try again later.');
        setLoading(false);
      }
    };

    setLoading(true);
    fetchFormFields();
  }, [formId]);

  const formatPhoneNumber = (value) => {
    const cleaned = `${value}`.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (match) {
      return [match[1], match[2], match[3]].filter(Boolean).join('-');
    }
    return value;
  };

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePhoneNumber = (phone) => /^\d{3}-\d{3}-\d{4}$/.test(phone);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const formattedValue = name === 'phone' ? formatPhoneNumber(value) : value;
    setFormValues((prev) => ({ ...prev, [name]: formattedValue }));
  };

  const handleInputBlur = (e) => {
    const { name, value } = e.target;
    const currentErrors = { ...errors };

    if (name === 'email' && value && !validateEmail(value)) {
      currentErrors.email = 'Please enter a valid email address.';
    } else if (name === 'phone' && value && !validatePhoneNumber(value)) {
      currentErrors.phone = 'Please enter a valid phone number (e.g. 123-123-1234).';
    } else {
      delete currentErrors[name];
    }

    setErrors(currentErrors);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmissionStatus(null);
    setError(null);

    const currentErrors = {};
    let hasErrors = false;

    formFields.forEach((field) => {
      const value = formValues[field.name] || '';

      if (field.required && !value) {
        currentErrors[field.name] = `${field.label} field is required.`;
        hasErrors = true;
      }

      if (field.name === 'email' && value && !validateEmail(value)) {
        currentErrors.email = 'Please enter a valid email address.';
        hasErrors = true;
      }

      if (field.name === 'phone' && value && !validatePhoneNumber(value)) {
        currentErrors.phone = 'Please enter a valid phone number (e.g. 123-123-1234).';
        hasErrors = true;
      }
    });

    if (hasErrors) {
      setErrors(currentErrors);
      return;
    }

    setErrors({});
    setLoading(true);

    const formData = new FormData(formRef.current);
    const formEntries = Object.fromEntries(formData.entries());
    const userIP = await getUserIP();
    const hutk = getHubSpotCookie();

    const smsConsentEntries = hasPhoneField ? smsConsentSubmissionFields(smsConsent) : {};

    const encodedFormData = qs.stringify({
      ...formEntries,
      ...smsConsentEntries,
      hs_context: JSON.stringify({
        hutk,
        ipAddress: userIP,
        pageUri: window.location.href,
        pageName: document.title,
      }),
    });

    const hubspotEndpoint = `https://forms.hubspot.com/uploads/form/v2/${PORTAL_ID}/${formId}`;

    try {
      await axios.post(hubspotEndpoint, encodedFormData, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      track('form_submitted', { formType, formId });
      setSubmitted(true);
      setSubmissionStatus('Success! Your information has been submitted.');
    } catch (err) {
      track('form_error', { formType, formId, errorType: 'submission_failed' });
      setSubmissionStatus('There was an error submitting the form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !formFields.length) {
    return (
      <div className="why-system-page grid-bg contact-form-page">
        <section className="sec contact-form-page__main">
          <div className="wrap">
            <div className="contact-form-page__card contact-form-page__card--center">
              <p className="monolabel">Loading form…</p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (error) {
    return <FormErrorState message="Cannot load form. Please try again later." onBack={() => navigate(-1)} />;
  }

  return (
    <div className="why-system-page grid-bg contact-form-page">
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      <section className="sec contact-form-page__main">
        <div className="wrap">
          <Link to="/interest" className="contact-form-page__back">
            ← All contact options
          </Link>

          <div className={`contact-form-page__shell${featured ? ' contact-form-page__shell--feat' : ''}`}>
            <div className="contact-form-page__intro">
              <span className="kicker">{kicker}</span>
              <h1 className="h2">
                {submitted ? successTitle : title}
                {!submitted && titleAccent ? <span className="rust"> {titleAccent}</span> : null}
              </h1>
              {!submitted ? <p className="lede">{lede}</p> : null}
            </div>

            <div className="contact-form-page__card">
              {submitted ? (
                <div className="contact-form-page__success">
                  <p>{successMessage}</p>
                  <div className="contact-form-page__actions">
                    <Link className="btn btn-primary lg" to="/interest">
                      Back to contact <ArrowIcon />
                    </Link>
                  </div>
                </div>
              ) : (
                <form ref={formRef} className="cx-form" onSubmit={handleSubmit}>
                  {formFields.map((field) => (
                    <div className="cx-field" key={field.name}>
                      <label className="cx-label" htmlFor={field.name}>
                        <span dangerouslySetInnerHTML={{ __html: field.label }} />
                        {field.required ? <span className="cx-required">*</span> : null}
                      </label>
                      {renderFieldInput(field, formValues, { handleInputChange, handleInputBlur })}
                      {errors[field.name] ? (
                        <p
                          className="cx-error"
                          dangerouslySetInnerHTML={{ __html: errors[field.name] }}
                        />
                      ) : null}
                    </div>
                  ))}

                  {hasPhoneField ? (
                    <SmsConsentFieldset consent={smsConsent} onChange={setSmsConsent} disabled={loading} />
                  ) : null}

                  {submissionStatus && !submissionStatus.startsWith('Success') ? (
                    <p className="cx-error cx-error--banner">{submissionStatus}</p>
                  ) : null}

                  <div className="contact-form-page__actions">
                    <button type="submit" className="btn btn-primary lg" disabled={loading}>
                      {loading ? 'Submitting…' : 'Submit'} {!loading ? <ArrowIcon /> : null}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
